import type { Signal} from '@angular/core';
import { Injectable, inject, signal } from '@angular/core';
import { onCLS, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';

import { AppConfigService } from '../config/app-config.service';
import { AuthStore } from '../auth/auth.store';
import { TenantContextService } from '../tenant/tenant-context.service';

type BeaconKind = 'uptime' | 'web-vital' | 'app-error';

interface BeaconPayload {
  readonly kind: BeaconKind;
  readonly ts: number;
  readonly sessionId: string;
  readonly tenantId: string | null;
  readonly userId: string | null;
  readonly url: string;
  readonly name?: string;
  readonly value?: number;
  readonly rating?: string;
  readonly online: boolean;
}

/**
 * SloBeacon: evidencia INDEPENDIENTE de disponibilidad (KPI app ≥99,5% mensual).
 *
 * Es una cláusula contractual dura: no alcanza con el uptime del backend, hay que
 * demostrar que la app estuvo servible desde el cliente. Este servicio:
 *  - Emite un latido de "estoy vivo" cada N seg (RUM de uptime) vía `sendBeacon`
 *    (no bloquea, sobrevive a `unload`, no consume el stack HTTP con interceptores).
 *  - Reporta Core Web Vitals (LCP/INP/CLS/TTFB) como señal de calidad percibida.
 *  - Respeta `sampleRate` para no saturar el colector.
 *
 * Deliberadamente usa `fetch`/`sendBeacon` crudos: el colector de SLO NO debe
 * pasar por auth/tenant/retry (mediría la salud del propio stack que evalúa).
 */
@Injectable({ providedIn: 'root' })
export class SloBeaconService {
  private readonly appConfig = inject(AppConfigService);
  private readonly auth = inject(AuthStore);
  private readonly tenant = inject(TenantContextService);

  private readonly sessionId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `sess-${Date.now()}`;

  private readonly _online = signal(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  readonly online: Signal<boolean> = this._online.asReadonly();

  private heartbeat: ReturnType<typeof setInterval> | undefined;
  private sampled = false;

  /** Arranca el RUM. Se invoca una vez desde el APP_INITIALIZER de observabilidad. */
  async start(): Promise<void> {
    // Los APP_INITIALIZER async corren en paralelo: esperar la config
    // explícitamente (load() es idempotente) en vez de asumir orden.
    await this.appConfig.load();
    const cfg = this.appConfig.config().observability;
    this.sampled = Math.random() < (cfg.sampleRate ?? 1);
    if (!this.sampled) return;

    // Estado de red del navegador → parte de la evidencia de disponibilidad.
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this._online.set(true));
      window.addEventListener('offline', () => this._online.set(false));
      // Último latido best-effort al cerrar/ocultar la pestaña.
      window.addEventListener('pagehide', () => this.send('uptime'));
    }

    // Latido de uptime.
    const periodMs = (cfg.heartbeatSeconds ?? 60) * 1000;
    this.send('uptime'); // latido inicial (marca el arranque exitoso del shell)
    this.heartbeat = setInterval(() => this.send('uptime'), periodMs);

    // Core Web Vitals.
    onLCP((m) => this.reportVital(m));
    onINP((m) => this.reportVital(m));
    onCLS((m) => this.reportVital(m));
    onTTFB((m) => this.reportVital(m));
  }

  /** Reporta un error de app como señal de degradación (opcional). */
  reportError(name: string): void {
    if (this.sampled) this.send('app-error', { name });
  }

  stop(): void {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = undefined;
  }

  private reportVital(metric: Metric): void {
    this.send('web-vital', {
      name: metric.name,
      value: Math.round(metric.value),
      rating: metric.rating,
    });
  }

  private send(kind: BeaconKind, extra?: Partial<BeaconPayload>): void {
    if (!this.sampled) return;
    const cfg = this.appConfig.config().observability;
    if (!cfg.beaconUrl) return;

    const payload: BeaconPayload = {
      kind,
      ts: Date.now(),
      sessionId: this.sessionId,
      tenantId: this.tenant.tenantId(),
      userId: this.auth.user()?.id ?? null,
      url: typeof location !== 'undefined' ? location.pathname : '',
      online: this._online(),
      ...extra,
    };

    const body = JSON.stringify(payload);
    try {
      if (typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
        navigator.sendBeacon(cfg.beaconUrl, new Blob([body], { type: 'application/json' }));
      } else {
        void fetch(cfg.beaconUrl, {
          method: 'POST',
          body,
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
        });
      }
    } catch {
      // El beacon NUNCA debe romper la app; se descarta silenciosamente.
    }
  }
}
